import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIdentity } from '../../containers/IdentityContainer';
import { useActiveFBOs } from '../../containers/ActiveFBOContainer';
import { useApi } from '../../containers/ApiContainer';
import { ACTIVATION_STATE, UserActivation, ChecklistVariant } from './types';
import { ACTIVATION_STEPS, getCurrentStepIndex, getCompletedCount } from './checklistSteps';
import { useActivationStore } from '../../stores/useActivationStore';

/**
 * Hook for accessing and updating user activation state
 *
 * Uses Zustand store for shared state so all components see updates.
 *
 * Usage:
 * const { isActive, activation, recordAutoStackRun, recordStep } = useActivation();
 */
export function useActivation() {
  const { airplxIdentity } = useIdentity();
  const { activeFBO } = useActiveFBOs();
  const { postgrest } = useApi();

  const userId = airplxIdentity?.userid;

  // Use shared Zustand store instead of local useState
  const {
    activation,
    isLoading,
    error,
    setActivation,
    setIsLoading,
    setError,
    reset,
  } = useActivationStore();

  // Track current FBO to reset state when it changes
  const prevFboIdRef = useRef<string | null>(null);

  // Fetch activation state for current user in current FBO
  const fetchActivation = useCallback(async () => {
    if (!postgrest || !userId || !activeFBO?.id) {
      setActivation(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await postgrest
        .from('user_activation')
        .select('*')
        .eq('user_id', userId)
        .eq('fbo_id', activeFBO.id)
        .maybeSingle();

      setActivation(result.data as UserActivation | null);
    } catch (err) {
      console.error('Failed to fetch activation:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch activation'));
      setActivation(null);
    } finally {
      setIsLoading(false);
    }
  }, [postgrest, userId, activeFBO?.id, setActivation, setIsLoading, setError]);

  // Reset state when FBO changes to avoid showing stale data
  useEffect(() => {
    if (activeFBO?.id && prevFboIdRef.current && prevFboIdRef.current !== activeFBO.id) {
      reset();
    }
    prevFboIdRef.current = activeFBO?.id || null;
  }, [activeFBO?.id, reset]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchActivation();
  }, [fetchActivation]);

  // Record step completion (uses JWT user internally in SQL function)
  const recordStep = useCallback(
    async (step: 'add_aircraft' | 'save_favorite' | 'share_layout') => {
      if (!postgrest || !activeFBO?.id) {
        return;
      }
      try {
        await postgrest.rpc('record_activation_step', {
          p_fbo_id: activeFBO.id,
          p_step: step,
        });
        // Refetch to get updated state
        await fetchActivation();
      } catch (err) {
        console.error('Failed to record activation step:', err);
      }
    },
    [activeFBO?.id, postgrest, fetchActivation]
  );

  // Record AutoStack run (uses JWT user internally in SQL function)
  const recordAutoStackRun = useCallback(async () => {
    if (!postgrest || !activeFBO?.id) {
      return;
    }
    try {
      await postgrest.rpc('record_autostack_run', {
        p_fbo_id: activeFBO.id,
      });
      // Refetch to get updated state
      await fetchActivation();
    } catch (err) {
      console.error('Failed to record AutoStack run:', err);
    }
  }, [activeFBO?.id, postgrest, fetchActivation]);

  // Computed values
  const isActive = useMemo(() => {
    if (!activation) return false;
    // Show checklist when mode is on AND user hasn't reached deep activation
    return activation.activation_mode && activation.activation_state < ACTIVATION_STATE.DEEP;
  }, [activation]);

  const checklistVariant: ChecklistVariant = activation?.checklist_variant || 'sidebar';

  const currentStepIndex = useMemo(() => {
    if (!activation) return 0;
    return getCurrentStepIndex(activation);
  }, [activation]);

  const completedCount = useMemo(() => {
    if (!activation) return 0;
    return getCompletedCount(activation);
  }, [activation]);

  return {
    // Raw data
    activation,
    isLoading,
    error,
    refetch: fetchActivation,

    // Computed state
    isActive,
    checklistVariant,
    currentStepIndex,
    completedCount,
    totalSteps: ACTIVATION_STEPS.length,

    // Actions
    recordStep,
    recordAutoStackRun,
  };
}
